import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  acceptOwnerBooking,
  fetchOwnerBookingVisibility,
  fetchOwnerHotelBookings,
} from "../services/hotelOwnerAccessService";
import { AcceptanceProofFields, useAcceptanceProof } from "../components/AcceptanceProof";

// COMPLETED stays in the list so a stay already loaded still renders its label, but it is not
// offered as a tab or a stat - the server withholds completed stays from this portal for now.
const TABS = [
  { id: "ONGOING", label: "Ongoing" },
  { id: "UPCOMING", label: "Upcoming" },
  { id: "COMPLETED", label: "Completed" },
];
// Completed is shown now. The server decides which finished stays are old enough to withhold, so
// the tab simply lists whatever comes back.
const VISIBLE_TABS = TABS;

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDayLabel = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
};

// Acceptance times are stored in UTC without a zone marker, so mark them before showing local time.
const formatAcceptedAt = (value) => {
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(String(value)) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });
};

const rupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const statusVariant = (status) => {
  if (status === "ONGOING") return "success";
  if (status === "UPCOMING") return "primary";
  return "secondary";
};

// Room lines arrive one per category per night. A hotel reads them by night, so they are regrouped
// that way rather than shown as a flat list.
const roomsByDate = (rooms = []) => {
  const byDate = new Map();
  rooms.forEach((room) => {
    if (!byDate.has(room.stayDate)) byDate.set(room.stayDate, []);
    byDate.get(room.stayDate).push(room);
  });
  return Array.from(byDate.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
};

const awaitingAcceptance = (stay) => stay.hotelAcceptance === "AWAITING";

const StayCard = ({ stay, showPayments = true, onAccept, accepting = false, focused = false }) => {
  const nights = Number(stay.nights || 0);
  return (
    <Card
      id={`hop-stay-${stay.bookingId}-${stay.hotelId}`}
      className={`hop-stay-card mb-3 is-${String(stay.status || "").toLowerCase()}${focused ? " is-focused" : ""}`}
    >
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="hop-guest">
            {stay.customerName || "Guest"}
            <Badge bg={statusVariant(stay.status)} className="ms-2">
              {TABS.find((tab) => tab.id === stay.status)?.label || stay.status}
            </Badge>
            {awaitingAcceptance(stay) && (
              <Badge bg="warning" text="dark" className="ms-2">Waiting for your acceptance</Badge>
            )}
            {stay.hotelAcceptance === "ACCEPTED" && (
              <Badge bg="success" className="ms-2">
                <i className="bi bi-check2-circle me-1" aria-hidden="true" />
                Accepted
              </Badge>
            )}
          </div>
          <div className="small hop-muted">
            Booking #{stay.bookingId}
            {stay.guests ? ` · ${stay.guests} guest${Number(stay.guests) === 1 ? "" : "s"}` : ""}
            {stay.hotelName ? ` · ${stay.hotelName}` : ""}
          </div>
          {stay.hotelAcceptance === "ACCEPTED" && stay.acceptedByName && (
            <div className="small fw-semibold text-success">
              Accepted by {stay.acceptedByName}
              {stay.acceptedAt ? ` on ${formatAcceptedAt(stay.acceptedAt)}` : ""}
            </div>
          )}
        </div>
        <div className="text-md-end small">
          <div className="fw-semibold" style={{ color: "#0f172a" }}>
            {formatDate(stay.checkInDate)} → {formatDate(stay.checkOutDate)}
          </div>
          <div className="hop-muted">
            {nights} night{nights === 1 ? "" : "s"}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {awaitingAcceptance(stay) && (
          <div className="hop-accept-strip d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="small">
              <div className="fw-semibold">Please accept this booking</div>
              <div>Once accepted, this booking cannot be denied in future.</div>
            </div>
            <Button variant="success" disabled={accepting} onClick={() => onAccept?.(stay)}>
              {accepting ? <Spinner animation="border" size="sm" /> : (
                <>
                  <i className="bi bi-check-lg me-1" aria-hidden="true" />
                  Accept booking
                </>
              )}
            </Button>
          </div>
        )}
        <Row className="g-3">
          <Col md={7}>
            <div className="hop-block-label mb-2">
              <i className="bi bi-door-open me-1" aria-hidden="true" />
              Rooms
            </div>
            <div className="d-flex flex-column gap-2">
              {roomsByDate(stay.rooms).map(([date, rooms]) => (
                <div key={date} className="hop-night">
                  <div className="hop-night-date">{formatDayLabel(date)}</div>
                  {rooms.map((room, index) => (
                    <div key={`${date}-${room.categoryName}-${index}`} className="hop-room">
                      {room.roomNumbers?.length
                        ? `Room${room.roomNumbers.length === 1 ? "" : "s"} ${room.roomNumbers.join(", ")}`
                        : `${room.units} room${Number(room.units) === 1 ? "" : "s"}`}
                      {room.categoryName ? ` - ${room.categoryName}` : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {!stay.rooms?.length && <div className="small hop-muted">No rooms recorded.</div>}
          </Col>
          <Col md={5}>
            <div className="hop-block-label mb-2">
              <i className="bi bi-person-badge me-1" aria-hidden="true" />
              Driver
            </div>
            {stay.driverPhone || stay.driverName ? (
              <div className="hop-driver small">
                <div className="fw-semibold">{stay.driverName || "Driver"}</div>
                {stay.driverPhone && (
                  <a href={`tel:${stay.driverPhone}`} className="text-decoration-none">
                    <i className="bi bi-telephone-fill me-1" aria-hidden="true" />
                    {stay.driverPhone}
                  </a>
                )}
                {stay.vehicleNumber && <div>Vehicle: {stay.vehicleNumber}</div>}
              </div>
            ) : (
              <div className="small hop-muted">Not assigned yet</div>
            )}

            {showPayments && <>
            <div className="hop-block-label mt-3 mb-2">
              <i className="bi bi-cash-coin me-1" aria-hidden="true" />
              Payments received
            </div>
            {stay.payments?.length ? (
              <div className="hop-payments">
                {stay.payments.map((payment, index) => (
                  <div
                    key={`${payment.paymentDate}-${index}`}
                    className="d-flex justify-content-between align-items-center small"
                  >
                    <span>
                      {formatDate(payment.paymentDate)}
                      {payment.paymentMethod ? ` · ${payment.paymentMethod}` : ""}
                    </span>
                    <span className="fw-semibold">{rupees(payment.amount)}</span>
                  </div>
                ))}
                <div className="d-flex justify-content-between align-items-center border-top mt-1 pt-1 small">
                  <span className="fw-semibold">Total paid</span>
                  <span className="hop-paid-total">{rupees(stay.totalPaid)}</span>
                </div>
              </div>
            ) : (
              <div className="small hop-muted">No payments recorded yet.</div>
            )}
            </>}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default function HotelOwnerBookings({ portalKey, token, hotelName, showPayments = true, portalLabel = "", onUnauthorized }) {
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ONGOING");

  // The first date the Completed tab covers; older trips are left out on purpose, so say so.
  const [completedFrom, setCompletedFrom] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    // Only a note on one tab - if it fails, the tab simply shows without it.
    fetchOwnerBookingVisibility(portalKey, token)
      .then((visibility) => setCompletedFrom(visibility?.completedFrom || null))
      .catch(() => setCompletedFrom(null));
    try {
      setStays(await fetchOwnerHotelBookings(portalKey, token));
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onUnauthorized?.();
        return;
      }
      toast.error(error?.response?.data?.message || "Could not load your bookings");
    } finally {
      setLoading(false);
    }
  }, [portalKey, token, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const byStatus = useMemo(() => {
    const groups = { ONGOING: [], UPCOMING: [], COMPLETED: [] };
    stays.forEach((stay) => {
      if (groups[stay.status]) groups[stay.status].push(stay);
    });
    return groups;
  }, [stays]);

  // Land on whichever tab actually has something, so the page is not empty on open. Only on the
  // first load - after that the chosen tab is the reader's, and Refresh must not move it.
  const tabChosenRef = useRef(false);
  useEffect(() => {
    if (tabChosenRef.current || !stays.length) return;
    tabChosenRef.current = true;
    if (byStatus.ONGOING.length) setActiveTab("ONGOING");
    else if (byStatus.UPCOMING.length) setActiveTab("UPCOMING");
    // Completed last: a hotel with nothing current still opens on something rather than a blank tab.
    else if (byStatus.COMPLETED.length) setActiveTab("COMPLETED");
  }, [byStatus, stays.length]);

  const visible = byStatus[activeTab] || [];
  const awaitingStays = useMemo(() => stays.filter(awaitingAcceptance), [stays]);
  const awaitingIn = (tabId) => (byStatus[tabId] || []).filter(awaitingAcceptance).length;

  // The stay whose Accept button was pressed, held until the hotel confirms in the dialog.
  const [confirmStay, setConfirmStay] = useState(null);
  const [acceptingKey, setAcceptingKey] = useState(null);
  const stayKey = (stay) => `${stay.bookingId}-${stay.hotelId}`;
  const proofState = useAcceptanceProof(!!confirmStay);

  const acceptStay = async () => {
    const stay = confirmStay;
    if (!stay || !proofState.proof) return;
    setAcceptingKey(stayKey(stay));
    try {
      const result = await acceptOwnerBooking(portalKey, token, stay.bookingId, stay.hotelId, proofState.proof);
      proofState.markUsed();
      setStays((current) => current.map((item) => (stayKey(item) === stayKey(stay)
        ? {
          ...item,
          hotelAcceptance: result?.hotelAcceptance || "ACCEPTED",
          acceptedAt: result?.acceptedAt,
          acceptedByName: result?.acceptedByName,
        }
        : item)));
      setConfirmStay(null);
      toast.success("Thank you - booking accepted");
    } catch (error) {
      if (error?.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      toast.error(error?.response?.data?.message || "Could not accept the booking");
    } finally {
      setAcceptingKey(null);
    }
  };

  // Show switches to the tab holding the first booking to accept and brings that card into view.
  // Switching alone did nothing visible when that tab was already open.
  const [focusKey, setFocusKey] = useState(null);
  const showFirstAwaiting = () => {
    const first = awaitingStays[0];
    if (!first) return;
    setActiveTab(first.status);
    setFocusKey(stayKey(first));
  };

  useEffect(() => {
    if (!focusKey) return undefined;
    // Runs after the tab change has rendered, so the card is on the page by now.
    document.getElementById(`hop-stay-${focusKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setFocusKey(null), 2400);
    return () => clearTimeout(timer);
  }, [focusKey, activeTab]);

  return (
    <Container fluid className="px-0">
      <div className="hop-hero">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            {portalLabel && <div className="hop-portal-label">{portalLabel}</div>}
            <h1>{hotelName || "My Bookings"}</h1>
            <div className="hop-hero-sub">Guests booked with you through IO Trips</div>
          </div>
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : (
              <>
                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
                Refresh
              </>
            )}
          </Button>
        </div>
        {/* The tiles are the tabs: tapping one lists those stays, so there is no second tab row. */}
        <div className="hop-stats" role="tablist" aria-label="Bookings">
          {VISIBLE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`hop-stat${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <div className="hop-stat-value">{byStatus[tab.id].length}</div>
              <div className="hop-stat-label">{tab.label}</div>
              {awaitingIn(tab.id) > 0 && (
                <Badge bg="warning" text="dark" pill className="hop-stat-alert">{awaitingIn(tab.id)} to accept</Badge>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="px-3 pt-3">
        {awaitingStays.length > 0 && (
          <Alert variant="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
            <span className="fw-semibold">
              <i className="bi bi-bell-fill me-2" aria-hidden="true" />
              {awaitingStays.length === 1
                ? "1 booking is waiting for your acceptance"
                : `${awaitingStays.length} bookings are waiting for your acceptance`}
            </span>
            <Button size="sm" variant="dark" onClick={showFirstAwaiting}>Show</Button>
          </Alert>
        )}

      {loading && !stays.length && (
        <div className="d-flex align-items-center gap-2 hop-muted">
          <Spinner animation="border" size="sm" /> Loading your bookings...
        </div>
      )}

      {activeTab === "COMPLETED" && completedFrom && (
        <Alert variant="info" className="py-2 small hop-completed-note">
          <i className="bi bi-info-circle me-2" aria-hidden="true" />
          Showing trips completed from <strong>{formatDate(completedFrom)}</strong>. For older trips, please contact
          IO Trips.
        </Alert>
      )}

      {!loading && !visible.length && (
        <Alert variant="light" className="border">
          No {TABS.find((tab) => tab.id === activeTab)?.label.toLowerCase()} bookings.
        </Alert>
      )}

        {visible.map((stay) => (
          <StayCard
            key={stayKey(stay)}
            stay={stay}
            showPayments={showPayments}
            onAccept={setConfirmStay}
            accepting={acceptingKey === stayKey(stay)}
            focused={focusKey === stayKey(stay)}
          />
        ))}
      </div>

      <Modal show={!!confirmStay} onHide={() => !acceptingKey && setConfirmStay(null)} centered>
        <Modal.Header closeButton={!acceptingKey}>
          <Modal.Title>Accept booking #{confirmStay?.bookingId}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmStay && (
            <>
              <div className="fw-semibold">{confirmStay.customerName || "Guest"}</div>
              <div className="small mb-3">
                {formatDate(confirmStay.checkInDate)} → {formatDate(confirmStay.checkOutDate)}
                {confirmStay.hotelName ? ` · ${confirmStay.hotelName}` : ""}
              </div>
            </>
          )}
          <Alert variant="warning" className="mb-3 small">
            Once accepted, this booking cannot be denied in future. Please keep these rooms for this guest.
          </Alert>
          <AcceptanceProofFields state={proofState} disabled={!!acceptingKey} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={!!acceptingKey} onClick={() => setConfirmStay(null)}>
            Cancel
          </Button>
          <Button variant="success" disabled={!!acceptingKey || !proofState.proof} onClick={acceptStay}>
            {acceptingKey ? <Spinner animation="border" size="sm" /> : "Accept booking"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
